import React, { useState, useCallback } from "react";
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
} from "@material-ui/core";
import {
  Storefront as StorefrontIcon,
  Star as StarIcon,
  Send as SendIcon,
  Category as CategoryIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import ProductSelectionModal from "../ProductSelectionModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";
import usePlanPermissions from "../../hooks/usePlanPermissions";

const useStyles = makeStyles((theme) => ({
  catalogButton: {
    color: theme.palette.primary.main,
  },
  menuItem: {
    minWidth: 200,
  },
}));

const CatalogActionBar = ({
  ticketId,
  ticketStatus,
  disabled = false,
  onProductSent,
  onCatalogSent
}) => {
  const classes = useStyles();
  const { permissions } = usePlanPermissions();
  const [anchorEl, setAnchorEl] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(false);

  const showCatalog = permissions.useWhatsappCatalog;
  const isMenuOpen = Boolean(anchorEl);

  const handleMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    fetchFeaturedProducts();
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const fetchFeaturedProducts = useCallback(async () => {
    if (!ticketId) return;
    
    setLoadingFeatured(true);
    try {
      const response = await api.get(`/tickets/${ticketId}/catalog/featured`);
      setFeaturedProducts(response.data.products || []);
    } catch (error) {
      console.error("Error fetching featured products:", error);
    } finally {
      setLoadingFeatured(false);
    }
  }, [ticketId]);

  const handleProductSelect = useCallback(async (product) => {
    try {
      const response = await api.post(`/tickets/${ticketId}/catalog/send-product`, {
        productId: product.id,
      });
      
      // Log the product sending action
      const logEntry = {
        timestamp: new Date().toISOString(),
        product: product,
        action: "product-sent",
      };
      
      if (onProductSent) {
        onProductSent(product, logEntry);
      }
      
      toastUtils.success(`Produto "${product.name}" enviado com sucesso!`, "Catálogo");
      return response.data;
    } catch (error) {
      console.error("Error sending product:", error);
      toastError("Erro ao enviar produto");
      throw error;
    }
  }, [ticketId, onProductSent]);

  const handleCatalogSend = useCallback(async () => {
    try {
      const response = await api.post(`/tickets/${ticketId}/catalog/send-catalog`);
      
      // Log the catalog sending action
      const logEntry = {
        timestamp: new Date().toISOString(),
        action: "catalog-sent",
      };
      
      if (onCatalogSent) {
        onCatalogSent(logEntry);
      }
      
      toastUtils.success("Catálogo enviado com sucesso!", "Catálogo");
      return response.data;
    } catch (error) {
      console.error("Error sending catalog:", error);
      toastError("Erro ao enviar catálogo");
      throw error;
    }
  }, [ticketId, onCatalogSent]);

  const handleFeaturedProductClick = useCallback(async (product) => {
    handleMenuClose();
    try {
      await handleProductSelect(product);
    } catch (error) {
      // Error already handled in handleProductSelect
    }
  }, [handleProductSelect, handleMenuClose]);

  const handleOpenProductModal = useCallback(() => {
    handleMenuClose();
    setProductModalOpen(true);
  }, [handleMenuClose]);

  const handleSendCatalogDirect = useCallback(async () => {
    handleMenuClose();
    try {
      await handleCatalogSend();
    } catch (error) {
      // Error already handled in handleCatalogSend
    }
  }, [handleCatalogSend, handleMenuClose]);

  // Don't render if ticket is not open, disabled, or catalog not allowed in plan
  if (ticketStatus !== "open" || disabled || !showCatalog) {
    return null;
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  return (
    <>
      <Tooltip title="Catálogo de Produtos">
        <IconButton
          aria-label="catalog-actions"
          onClick={handleMenuOpen}
          className={classes.catalogButton}
        >
          <Badge 
            badgeContent={featuredProducts.length > 0 ? featuredProducts.length : null} 
            color="secondary"
          >
            <StorefrontIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <MenuItem onClick={handleOpenProductModal} className={classes.menuItem}>
          <ListItemIcon>
            <CategoryIcon />
          </ListItemIcon>
          <ListItemText primary="Buscar Produtos" secondary="Navegar por todo o catálogo" />
        </MenuItem>

        <MenuItem onClick={handleSendCatalogDirect} className={classes.menuItem}>
          <ListItemIcon>
            <StorefrontIcon />
          </ListItemIcon>
          <ListItemText primary="Enviar Catálogo Completo" secondary="Enviar link do catálogo" />
        </MenuItem>

        {featuredProducts.length > 0 && (
          <>
            <MenuItem disabled className={classes.menuItem}>
              <ListItemIcon>
                <StarIcon />
              </ListItemIcon>
              <ListItemText primary="Produtos em Destaque" />
            </MenuItem>
            
            {featuredProducts.slice(0, 5).map((product) => (
              <MenuItem
                key={product.id}
                onClick={() => handleFeaturedProductClick(product)}
                className={classes.menuItem}
              >
                <ListItemIcon>
                  <SendIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={product.name}
                  secondary={formatPrice(product.price)}
                />
              </MenuItem>
            ))}
            
            {featuredProducts.length > 5 && (
              <MenuItem onClick={handleOpenProductModal} className={classes.menuItem}>
                <ListItemIcon>
                  <StarIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={`Ver mais ${featuredProducts.length - 5} produtos`}
                  secondary="Abrir catálogo completo"
                />
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      <ProductSelectionModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        onProductSelect={handleProductSelect}
        onCatalogSend={handleCatalogSend}
        ticketId={ticketId}
      />
    </>
  );
};

export default CatalogActionBar;